import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useIsAdmin } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Edit3, Trash2, ShieldAlert, X, ExternalLink } from "lucide-react";

/**
 * AdminQuickActions — inline Edit + Soft/Hard Delete affordances usable on
 * any detail page (coach profile, job posting, …). Hidden when the viewer
 * isn't a platform admin.
 *
 * Usage:
 *   <AdminQuickActions
 *     entity="coach"                  // "coach" | "job"
 *     id={coach.id}
 *     name={coach.name}
 *     fields={[
 *       { key: "name", label: "Name", value: coach.name },
 *       { key: "headline", label: "Headline", value: coach.headline, multiline: true },
 *       …
 *     ]}
 *     onSaved={(updated) => setCoach(updated)}
 *     onDeleted={() => navigate("/talent")}
 *   />
 */
export default function AdminQuickActions({
  entity,           // "coach" | "job"
  id,
  name,
  fields,           // [{ key, label, value, multiline? }]
  onSaved,
  onDeleted,
}) {
  const isAdmin = useIsAdmin();
  const nav = useNavigate();
  const [showEdit, setShowEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, f.value ?? ""]))
  );

  if (!isAdmin) return null;

  const apiPath = entity === "job" ? `/admin/jobs/${id}` : `/admin/coaches/${id}`;
  const noun = entity === "job" ? "job posting" : "coach profile";

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      // Strip empties so we don't blank out fields the admin didn't touch.
      Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
      const { data } = await api.patch(apiPath, body);
      toast.success(`${noun} updated`);
      setShowEdit(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const softDelete = async () => {
    if (!window.confirm(`Soft-delete ${name || "this " + noun}? It will be hidden from every public listing but kept for restoration.`)) return;
    setBusy(true);
    try {
      await api.delete(apiPath);
      toast.success(`${noun} removed from public view`);
      if (onDeleted) onDeleted();
      else nav(entity === "job" ? "/ad" : "/talent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const hardDelete = async () => {
    if (!window.confirm(`PERMANENTLY delete ${name || "this " + noun}? This cannot be undone.`)) return;
    const phrase = window.prompt("Type DELETE to confirm hard delete:");
    if (phrase !== "DELETE") {
      toast.error("Cancelled — confirmation phrase mismatch.");
      return;
    }
    setBusy(true);
    try {
      await api.delete(`${apiPath}?hard=true`);
      toast.success(`${noun} permanently deleted`);
      if (onDeleted) onDeleted();
      else nav(entity === "job" ? "/ad" : "/talent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hard delete failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div
        className="inline-flex items-center gap-2 border border-volt bg-[#0A101A] px-3 py-1.5"
        data-testid="admin-quick-actions"
      >
        <ShieldAlert className="text-volt" size={14} />
        <span className="text-[10px] tracking-widest text-volt">ADMIN TOOLS</span>
        <button
          onClick={() => setShowEdit(true)}
          className="text-xs heading tracking-widest text-neutral-300 hover:text-volt px-2 py-1 flex items-center gap-1"
          data-testid="admin-edit-btn"
        >
          <Edit3 size={12} /> EDIT
        </button>
        {(entity === "coach" || entity === "job") && (
          <Link
            to={entity === "coach" ? `/ad/marketing/coaches/${id}/edit` : `/ad/marketing/jobs/${id}/edit`}
            data-testid="admin-full-editor-link"
            className="text-xs heading tracking-widest text-neutral-300 hover:text-volt px-2 py-1 flex items-center gap-1"
            title="Open full-page editor with every field"
          >
            <ExternalLink size={12} /> ALL FIELDS
          </Link>
        )}
        <button
          onClick={softDelete}
          disabled={busy}
          className="text-xs heading tracking-widest text-neutral-300 hover:text-blaze px-2 py-1 flex items-center gap-1 disabled:opacity-50"
          data-testid="admin-delete-btn"
        >
          <Trash2 size={12} /> DELETE
        </button>
      </div>

      {showEdit && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowEdit(false)}
          data-testid="admin-quick-edit-modal"
        >
          <div className="bg-[#0A101A] border border-volt max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-[10px] tracking-widest text-volt">ADMIN · QUICK EDIT</div>
                <div className="heading text-2xl mt-1">{name || `Untitled ${noun}`}</div>
              </div>
              <button onClick={() => setShowEdit(false)} className="text-neutral-500 hover:text-white" data-testid="admin-edit-close">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.key} className={f.multiline ? "md:col-span-2" : ""}>
                  <label className="text-[10px] tracking-widest text-neutral-500">{f.label.toUpperCase()}</label>
                  {f.multiline ? (
                    <textarea
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      data-testid={`admin-edit-${f.key}`}
                      rows={4}
                      className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
                    />
                  ) : (
                    <input
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      data-testid={`admin-edit-${f.key}`}
                      className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-5">
              <button
                onClick={hardDelete}
                disabled={busy}
                className="text-xs tracking-widest text-red-500 hover:text-red-400 flex items-center gap-1 disabled:opacity-50"
                data-testid="admin-hard-delete-btn"
              >
                <Trash2 size={12} /> HARD DELETE (PERMANENT)
              </button>
              <div className="flex gap-2">
                <button onClick={() => setShowEdit(false)} className="px-4 py-2 text-xs heading tracking-widest text-neutral-400 hover:text-white">
                  CANCEL
                </button>
                <button onClick={save} disabled={busy} className="px-4 py-2 bg-volt text-white text-xs heading tracking-widest hover:glow-volt disabled:opacity-50" data-testid="admin-save-btn">
                  {busy ? "SAVING…" : "SAVE"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
